/* eslint-disable */

import React, { useEffect, useMemo, useState } from 'react'
import PageContainer from '../../components/PageContainer/PageContainer'
import { useAppDispatch, useAppSelector } from '../../redux/hooks'
import {
  acceptMenuCandidate,
  autoProcessAllMissingMenus,
  fetchMissingMenuQueue,
  rejectMenuCandidate,
  scanMissingMenuWebsite,
  selectMissingMenuCount,
  selectMissingMenuIsLoading,
  selectMissingMenuItems,
  selectMissingMenuPage,
  selectMissingMenuPageSize,
  setPage,
  setPageSize,
} from './slice'
import {
  CardActions,
  CardInfo,
  CandidateList,
  CandidateRow,
  CandidateUrl,
  Container,
  ContentBody,
  ContentHeader,
  PageSizeControl,
  PaginationContainer,
  PaginationControls,
  PaginationInfo,
  QueueCard,
  QueueGrid,
  ScoreBadge,
  SearchContainer,
  StatusBadge,
  Title,
} from './MissingMenuQueue.styled'
import Button from '@mui/material/Button'
import TextField from '@mui/material/TextField'
import Select from '@mui/material/Select'
import MenuItem from '@mui/material/MenuItem'
import FormControl from '@mui/material/FormControl'
import InputLabel from '@mui/material/InputLabel'
import CircularProgress from '@mui/material/CircularProgress'
import { withAuthenticationRequired } from '@auth0/auth0-react'
import { MissingMenuRestaurant } from './types'

const MissingMenuQueue: React.FC = () => {
  const dispatch = useAppDispatch()
  const items = useAppSelector(selectMissingMenuItems)
  const count = useAppSelector(selectMissingMenuCount)
  const page = useAppSelector(selectMissingMenuPage)
  const pageSize = useAppSelector(selectMissingMenuPageSize)
  const isLoading = useAppSelector(selectMissingMenuIsLoading)
  const [searchQuery, setSearchQuery] = useState('')
  const [scanningIds, setScanningIds] = useState<Set<string>>(new Set())
  const [open, setOpen] = useState(false)

  useEffect(() => {
    dispatch(fetchMissingMenuQueue(0))
  }, [dispatch])

  const filteredItems = useMemo(() => {
    if (!searchQuery.trim()) return items
    const q = searchQuery.toLowerCase()
    return items.filter(
      (item) =>
        (item.name?.toLowerCase().includes(q) ?? false) ||
        (item.city?.toLowerCase().includes(q) ?? false) ||
        (item.state?.toLowerCase().includes(q) ?? false) ||
        (item.website?.toLowerCase().includes(q) ?? false),
    )
  }, [items, searchQuery])

  const totalPages = Math.ceil(count / pageSize)
  const startIndex = page * pageSize + 1
  const endIndex = Math.min((page + 1) * pageSize, count)
  const hasNextPage = page < totalPages - 1
  const hasPreviousPage = page > 0

  const handleNextPage = () => {
    if (hasNextPage) {
      dispatch(setPage(page + 1))
      dispatch(fetchMissingMenuQueue(page + 1))
    }
  }

  const handlePreviousPage = () => {
    if (hasPreviousPage) {
      dispatch(setPage(page - 1))
      dispatch(fetchMissingMenuQueue(page - 1))
    }
  }

  const handlePageSizeChange = (event: any) => {
    dispatch(setPageSize(event.target.value))
  }

  const handleScan = async (item: MissingMenuRestaurant) => {
    setScanningIds((prev) => new Set(prev).add(item.restaurantId))
    await dispatch(scanMissingMenuWebsite(item.restaurantId))
    setScanningIds((prev) => {
      const next = new Set(prev)
      next.delete(item.restaurantId)
      return next
    })
  }

  const handleAccept = (restaurantId: string, candidateId: string) => {
    dispatch(acceptMenuCandidate({ restaurantId, candidateId }))
  }

  const handleReject = (restaurantId: string, candidateId: string) => {
    dispatch(rejectMenuCandidate({ restaurantId, candidateId }))
  }

  const handleAutoProcessAll = () => {
    void dispatch(autoProcessAllMissingMenus()).then(() => {
      dispatch(fetchMissingMenuQueue(page))
    })
  }

  return (
    <PageContainer title="Missing Menu Queue" open={open} setOpen={setOpen}>
      <Container>
        <ContentHeader>
          <Title>Missing Menu Queue</Title>
          <Button
            variant="contained"
            disabled={isLoading}
            onClick={handleAutoProcessAll}
            sx={{
              backgroundColor: '#7c3aed',
              '&:hover': { backgroundColor: '#6d28d9' },
              fontSize: '13px',
              fontWeight: 600,
              whiteSpace: 'nowrap',
            }}
          >
            {isLoading ? (
              <>
                <CircularProgress size={14} sx={{ mr: 1, color: '#fff' }} />
                Processing…
              </>
            ) : (
              'Auto-Process All'
            )}
          </Button>
        </ContentHeader>

        <SearchContainer>
          <TextField
            label="Search"
            variant="outlined"
            size="small"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Search by restaurant name, city, state, or URL"
            fullWidth
            sx={{ maxWidth: '400px' }}
          />
        </SearchContainer>

        {isLoading ? (
          <div style={{ display: 'flex', justifyContent: 'center', padding: '40px' }}>
            <CircularProgress />
          </div>
        ) : (
          <ContentBody>
            <QueueGrid>
              {filteredItems.map((item) => {
                const isScanning = scanningIds.has(item.restaurantId)
                return (
                  <QueueCard key={item.restaurantId}>
                    <CardInfo>
                      <div>
                        <strong>{item.name ?? '—'}</strong>
                      </div>
                      <div>
                        Location: {item.city ?? '—'}, {item.state ?? '—'}
                      </div>
                      <div>
                        Website:{' '}
                        {item.website ? (
                          <CandidateUrl
                            href={item.website}
                            target="_blank"
                            rel="noopener noreferrer"
                          >
                            {item.website}
                          </CandidateUrl>
                        ) : (
                          '—'
                        )}
                      </div>
                    </CardInfo>

                    <CardActions>
                      <Button
                        variant="contained"
                        size="small"
                        disabled={isScanning || !item.website}
                        onClick={() => handleScan(item)}
                        sx={{
                          backgroundColor: '#ff8c42',
                          '&:hover': { backgroundColor: '#e07a35' },
                          fontSize: '12px',
                        }}
                      >
                        {isScanning ? (
                          <>
                            <CircularProgress size={12} sx={{ mr: 1, color: '#fff' }} />
                            Scanning…
                          </>
                        ) : (
                          'Scan Website'
                        )}
                      </Button>
                    </CardActions>

                    {item.candidates !== undefined && (
                      <CandidateList>
                        {item.candidates.length === 0 ? (
                          <div style={{ fontSize: '12px', color: '#666' }}>
                            No menu links found.
                          </div>
                        ) : (
                          item.candidates.map((candidate) => (
                            <CandidateRow key={candidate.candidateId}>
                              <ScoreBadge score={candidate.score}>{candidate.score}</ScoreBadge>
                              <CandidateUrl
                                href={candidate.candidate_url}
                                target="_blank"
                                rel="noopener noreferrer"
                                title={candidate.anchor_text}
                              >
                                {candidate.anchor_text
                                  ? `${candidate.anchor_text} — ${candidate.candidate_url}`
                                  : candidate.candidate_url}
                              </CandidateUrl>
                              <StatusBadge status={candidate.status}>
                                {candidate.status}
                              </StatusBadge>
                              {candidate.status === 'PENDING' && (
                                <>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="success"
                                    sx={{ fontSize: '11px', py: 0.25, minWidth: 0 }}
                                    onClick={() =>
                                      handleAccept(item.restaurantId, candidate.candidateId)
                                    }
                                  >
                                    Accept
                                  </Button>
                                  <Button
                                    size="small"
                                    variant="outlined"
                                    color="error"
                                    sx={{ fontSize: '11px', py: 0.25, minWidth: 0 }}
                                    onClick={() =>
                                      handleReject(item.restaurantId, candidate.candidateId)
                                    }
                                  >
                                    Reject
                                  </Button>
                                </>
                              )}
                            </CandidateRow>
                          ))
                        )}
                      </CandidateList>
                    )}
                  </QueueCard>
                )
              })}
            </QueueGrid>
          </ContentBody>
        )}

        {count > 0 && (
          <PaginationContainer>
            <PaginationInfo>
              Showing {count > 0 ? startIndex : 0} to {endIndex} of {count} restaurants
            </PaginationInfo>

            <PaginationControls>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasPreviousPage}
                onClick={handlePreviousPage}
              >
                Previous
              </Button>
              <PaginationInfo>
                Page {page + 1} of {totalPages}
              </PaginationInfo>
              <Button
                variant="outlined"
                size="small"
                disabled={!hasNextPage}
                onClick={handleNextPage}
              >
                Next
              </Button>
            </PaginationControls>

            <PageSizeControl>
              <FormControl size="small" fullWidth>
                <InputLabel>Items per page</InputLabel>
                <Select value={pageSize} onChange={handlePageSizeChange} label="Items per page">
                  <MenuItem value={10}>10</MenuItem>
                  <MenuItem value={20}>20</MenuItem>
                  <MenuItem value={50}>50</MenuItem>
                  <MenuItem value={100}>100</MenuItem>
                </Select>
              </FormControl>
            </PageSizeControl>
          </PaginationContainer>
        )}
      </Container>
    </PageContainer>
  )
}

export default withAuthenticationRequired(MissingMenuQueue)
